import * as Sentry from '@sentry/vue'

// Cache promises to avoid duplicate work across multiple composable users
let loadScriptPromise = null
let detectPromise = null

// Safely load WeChat JSSDK only once (and only when needed)
function loadWeChatSdkOnce() {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return Promise.resolve()
  }

  if (loadScriptPromise) return loadScriptPromise

  loadScriptPromise = new Promise((resolve, reject) => {
    // If script already exists in DOM, resolve immediately
    const existing = document.getElementById('wx-jssdk')
    if (existing) {
      resolve()
      return
    }

    const script = document.createElement('script')
    script.id = 'wx-jssdk'
    script.src = 'https://res.wx.qq.com/open/js/jweixin-1.6.0.js'
    script.async = true
    script.onload = () => resolve()
    script.onerror = (error) => {
      Sentry.captureException(
        new Error(
          `WeChat jweixin script failed to load: ${error?.message || 'unknown error'}`
        )
      )
      reject(error)
    }
    document.head.appendChild(script)
  })

  return loadScriptPromise
}

export async function detectWeChatMiniProgram() {
  if (detectPromise) return detectPromise

  detectPromise = (async () => {
    try {
      if (typeof window === 'undefined' || typeof navigator === 'undefined') {
        return false
      }
      // https://developers.weixin.qq.com/miniprogram/en/dev/component/web-view.html
      const ua = (navigator.userAgent || '').toLowerCase()
      const hasMiniProgramHint =
        ua.includes('miniprogram') ||
        window.__wxjs_environment === 'miniprogram'
      const isWeChatUA = ua.includes('micromessenger')

      // Load SDK only if we're inside the WeChat environment
      if (hasMiniProgramHint || isWeChatUA) {
        await loadWeChatSdkOnce()
      }

      if (hasMiniProgramHint) {
        return true
      }

      // Try using official API if available
      const getEnv = window.wx?.miniProgram?.getEnv
      if (typeof getEnv === 'function') {
        return await new Promise((resolve) => {
          try {
            getEnv((res) => resolve(!!res?.miniprogram))
          } catch (_e) {
            Sentry.captureException(
              new Error(
                `WeChat getEnv callback failed: ${_e?.message || 'unknown error'}`
              )
            )
            resolve(false)
          }
        })
      } else {
        return false
      }
    } catch (error) {
      Sentry.captureException(
        new Error(
          `WeChat MiniProgram detection error: ${error?.message || 'unknown error'}`
        )
      )
      return false
    }
  })()

  return detectPromise
}

/**
 * Triggers a callback when WeChat's JSBridge is ready.
 * This is useful for triggering video autoplay or other WeChat-specific features.
 * @param {Function} callback
 */
export function onWeChatReady(callback) {
  if (typeof window === 'undefined') return

  const triggerCallback = () => {
    // Calling a bridge method like getNetworkType is a known hack to trigger autoplay
    window.WeixinJSBridge.invoke('getNetworkType', {}, () => {
      callback()
    })
  }

  if (typeof window.WeixinJSBridge !== 'undefined') {
    triggerCallback()
  } else {
    document.addEventListener(
      'WeixinJSBridgeReady',
      () => {
        triggerCallback()
      },
      false
    )
  }
}
